import React, { useEffect, useState } from "react";
import { Download, X, Share, Plus } from "lucide-react";

const DISMISS_KEY = "kiltro_pwa_install_dismissed_at";
const DISMISS_DAYS = 7;

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua)) return true;
  // iPadOS 13+ reports a Mac-like UA; detect via touch points
  if (/Mac/.test(ua) && navigator.maxTouchPoints > 1) return true;
  return false;
}

function isMobile() {
  if (typeof navigator === "undefined") return false;
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) return true;
  // iPadOS 13+ workaround
  if (/Mac/.test(navigator.userAgent) && navigator.maxTouchPoints > 1) return true;
  return false;
}

function wasRecentlyDismissed() {
  try {
    const ts = localStorage.getItem(DISMISS_KEY);
    if (!ts) return false;
    const diff = Date.now() - Number(ts);
    return diff < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

export default function InstallPWA() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (wasRecentlyDismissed()) return;
    if (!isMobile()) return; // only show on mobile

    // Android / Chrome path
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS path: no beforeinstallprompt, show after small delay
    if (isIOS()) {
      const t = setTimeout(() => setVisible(true), 3500);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      };
    }

    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {}
    setVisible(false);
    setShowIosHelp(false);
  };

  const install = async () => {
    if (deferred) {
      try {
        deferred.prompt();
        const choice = await deferred.userChoice;
        if (choice.outcome === "accepted") {
          setVisible(false);
        } else {
          dismiss();
        }
      } catch {
        dismiss();
      }
      setDeferred(null);
    } else if (isIOS()) {
      setShowIosHelp(true);
    }
  };

  if (!visible) return null;

  return (
    <>
      {/* iOS Help Modal */}
      {showIosHelp && (
        <div
          className="fixed inset-0 z-[9999] bg-black/85 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          data-testid="pwa-ios-help-modal"
          onClick={dismiss}
        >
          <div
            className="bg-black border-2 border-[#FEBA01] max-w-md w-full p-6 rounded-2xl shadow-[0_8px_0_0_rgba(254,186,1,0.6)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[#FEBA01] font-display text-xl tracking-wide" style={{ fontFamily: "Outfit, sans-serif", fontWeight: 800 }}>
                Instalar Kiltro
              </h3>
              <button
                onClick={dismiss}
                className="text-white/60 hover:text-white"
                data-testid="pwa-ios-close-btn"
                aria-label="Cerrar"
              >
                <X size={20} />
              </button>
            </div>
            <p className="text-white/80 text-sm mb-5" style={{ fontFamily: "Outfit, sans-serif" }}>
              Para instalar la app en tu iPhone:
            </p>
            <ol className="space-y-3 text-white text-sm" style={{ fontFamily: "Outfit, sans-serif" }}>
              <li className="flex items-start gap-3">
                <span className="bg-[#FEBA01] text-black w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs shrink-0">1</span>
                <span className="flex items-center gap-1.5 flex-wrap">
                  Toca el botón <Share size={16} className="inline text-[#FEBA01]" /> <strong>Compartir</strong> en la barra de Safari
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-[#FEBA01] text-black w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs shrink-0">2</span>
                <span className="flex items-center gap-1.5 flex-wrap">
                  Selecciona <Plus size={16} className="inline text-[#FEBA01]" /> <strong>"Añadir a pantalla de inicio"</strong>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-[#FEBA01] text-black w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs shrink-0">3</span>
                <span>
                  Toca <strong>"Añadir"</strong> y listo — tienes Kiltro en tu home.
                </span>
              </li>
            </ol>
            <button
              onClick={dismiss}
              className="mt-6 w-full bg-[#FEBA01] text-black font-bold py-3 rounded-xl uppercase tracking-wider hover:opacity-90 transition"
              style={{ fontFamily: "Outfit, sans-serif", fontWeight: 800 }}
              data-testid="pwa-ios-got-it-btn"
            >
              Entendido
            </button>
          </div>
        </div>
      )}

      {/* Bottom sticky bar */}
      <div
        className="fixed bottom-14 left-0 right-0 z-[9990] sm:bottom-4 sm:left-4 sm:right-auto sm:max-w-sm"
        data-testid="pwa-install-banner"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="bg-black border-t-2 sm:border-2 border-[#FEBA01] sm:rounded-2xl p-3 flex items-center gap-3 shadow-[0_-4px_0_0_rgba(254,186,1,0.4)] sm:shadow-[0_6px_0_0_rgba(254,186,1,0.5)]">
          <img
            src="/icon-192.png"
            alt="Kiltro"
            className="w-12 h-12 rounded-xl shrink-0 border border-[#FEBA01]/40"
          />
          <div className="flex-1 min-w-0">
            <div
              className="text-[#FEBA01] text-sm font-bold leading-tight"
              style={{ fontFamily: "Outfit, sans-serif", fontWeight: 800 }}
            >
              Instala Kiltro
            </div>
            <div
              className="text-white/70 text-xs leading-tight mt-0.5"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Acceso directo desde tu pantalla
            </div>
          </div>
          <button
            onClick={install}
            className="bg-[#FEBA01] text-black font-bold px-4 py-2 rounded-lg text-sm uppercase tracking-wide shrink-0 hover:opacity-90 transition flex items-center gap-1.5"
            style={{ fontFamily: "Outfit, sans-serif", fontWeight: 800 }}
            data-testid="pwa-install-btn"
          >
            <Download size={14} />
            Instalar
          </button>
          <button
            onClick={dismiss}
            className="text-white/50 hover:text-white p-1 shrink-0"
            aria-label="Cerrar"
            data-testid="pwa-dismiss-btn"
          >
            <X size={18} />
          </button>
        </div>
      </div>
    </>
  );
}
